import clsx from "clsx";
import { Button } from "components";
import { Sizes } from "enums/sizes";
import { useDialog } from "hooks/useDialog";
import { NotebookPen } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { useRoleCan } from "core/hooks/useRoleCan";
import { Capability } from "core/roles";
import NoteManager from "./NoteManager";
import { NoteInsuredSelector, type InsuredOption } from "./NoteInsuredSelector";

interface NoteButtonProps {
  riskId?: string
  insureds?: Array<InsuredOption>
  isBorrower: boolean
  /** Classes additionnelles du bouton (ex. w-full pour l'aligner dans une grille). */
  className?: string
}

export const NoteButton: FC<NoteButtonProps> = ({ riskId, insureds, isBorrower, className }) => {
  const { openBlankDialog } = useDialog();
  // Notes : droit piloté par la matrice centrale (Plateforme & co exclus).
  const canNotes = useRoleCan(Capability.NotesAccess);

  const handleBorrowerOpenNote = useCallback(() => {
    if (insureds && insureds.length > 0) {

      if (insureds?.length === 1) {
        openBlankDialog(
          {
            size: Sizes["5xl"]
          },
          <NoteManager riskId={insureds[0].borrowerId} autoOpenForm title={`Note de l'assuré - ${insureds[0].label}`} />
        );
        return
      }
      openBlankDialog(
        {
          size: Sizes["5xl"]
        },
        <NoteInsuredSelector insureds={insureds} />
      );
    }

  }, [insureds, openBlankDialog])

  const handleOpenNote = useCallback(() => {
    openBlankDialog(
      {
        size: Sizes["5xl"]
      },
      <NoteManager riskId={riskId} autoOpenForm />
    );
  }, [openBlankDialog, riskId]);

  const isButtonDisabled = useMemo(() => {
    if (isBorrower) {
      return (insureds === undefined || insureds.length === 0)
    }
    return false
  }, [insureds, isBorrower])

  // Rôle sans droit notes (ex. Plateforme) : pas de bouton.
  if (!canNotes) return null;

  return (
    <div className="relative">
      <Button
        round
        color="white"
        disabled={isButtonDisabled}
        onClick={isBorrower ? handleBorrowerOpenNote : handleOpenNote}
        className={clsx(
          '!px-2 !py-1.5 shadow-sm hover:ring-2 hover:ring-primary/20 transition-all text-sm',
          className
        )}
      >
        <NotebookPen
          data-slot="icon"
          className="text-primary !w-4 !h-4"
        />
        <span className="text-gray-700 whitespace-nowrap">Écrire une note</span>
      </Button>
    </div>
  )
}
